import * as Tone from 'tone';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useStore } from '@tmsw/powerups.store';
import { useTranslation } from '@tmsw/powerups.intl.hooks.use-translation';
// import { LanguageSwitcher } from '@tmsw/powerups.intl.components.language-switcher';
// import { TestingNav } from '@tmsw/powerups.debug.testing-nav';
import { useViewport } from '@tmsw/powerups.hooks.use-viewport';
// import { PageTitleAnnouncer } from '@tmsw/powerups.ui.page-title-announcer';
import { PageTitleAnnouncer } from '../page-title-announcer';
// import { NavBar } from '@tmsw/powerups.ui.nav-bar';
import { NavBar } from '../NavBar';
import { SettingsDialog } from '@tmsw/powerups.ui.dialogs.settings';
import { isInPortrait } from '@tmsw/powerups.utils.screen-info';
import { HiddenH1 } from '../HiddenH1/HiddenH1';
import { usePageConfig } from '@tmsw/powerups.config.page.provider';
import { useReactAudioContext } from '@tmsw/powerups.audio.provider';
// import BackButton from "components/BackButton";
import { SkipButton } from '../SkipButton/SkipButton';
import { PauseButton } from '../PauseButton/PauseButton';
import VIE from '../Templates/VIE';
import { OrientationChangeReminder } from '../OrientationChangeReminder';
import { PauseGeneral } from '../PauseGeneral';
// import useResetFocus from '../../hooks/useResetFocus';
// import { globalizedMedia } from '@tmsw/powerups.intl.hooks.use-localized-media';
import { useInitAudioDesc } from '../../hooks/useInitAudioDesc';
import { usePageNavigation } from '../../hooks/usePageNavigation';
import { getDisabledPageIDs } from '../../hooks/usePauseScreenDisabled';
import './GameWrapper.css';
import SettingsButtonGradient from '../SettingsButtonGradient/SettingsButtonGradient';
import useScoreboard from '../../pages/GamePlay/HexGame/hooks/useScoreboard';
import { focusSettingsToggler } from '../../services/focusSettingsToggler';

// Tone.setContext(new Tone.Context({ latencyHint: 'playback' }));

export type GameWrapperProps = {
  children: ReactNode;
};

export const GameWrapper = (props: GameWrapperProps) => {
  // global store
  const inPortrait = useStore((state) => state.inPortrait);
  const setInPortrait = useStore(
    useCallback((state) => state.setInPortrait, [])
  );
  const play = useStore((state) => state.play);
  const setPlay = useStore(useCallback((state) => state.setPlay, []));
  const isPauseEnabled = useStore((state) => state.isPauseEnabled);
  const setIsPauseEnabled = useStore(
    useCallback((state) => state.setIsPauseEnabled, [])
  );
  const setUserId = useStore(useCallback((state) => state.setUserId, []));
  const page = useStore((state) => state.currentPage);
  const isPauseMenuDisplayed = useStore((state) => state.isPauseMenuDisplayed);
  const setIsPauseMenuDisplayed = useStore(
    useCallback((state) => state.setIsPauseMenuDisplayed, [])
  );
  const documentVisibilityState = useStore(
    (state) => state.documentVisibilityState
  );
  const setDocumentVisibilityState = useStore(
    useCallback((state) => state.setDocumentVisibilityState, [])
  );
  const currentVIEConfig = useStore((state) => state.currentVIEConfig);
  const showSettingsPanel = useStore((state) => state.showSettingsPanel);

  // hooks
  const { __t } = useTranslation();
  const { audioCtxStarted } = useReactAudioContext();
  useInitAudioDesc(); // initialize audio desciptions api
  const viewport = useViewport(); // initialize viewport hook

  // refs
  const videoRef = useRef();
  const currentCueRef = useRef('');

  // console.log('currentVIEConfig', currentVIEConfig);

  const { goToPage, goToNextPage, goToPrevPage } = usePageNavigation();

  const { pagesMap, getPageKey, getPageId } = usePageConfig();

  const pageKey = getPageKey(page);

  const pauseScreenDisabledPagesID = useCallback(
    () =>
      getDisabledPageIDs(__t('disablePauseScreenOn', 'PAUSE_MENU'), getPageId),
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    []
  )();

  /** GAMEBOARD HACK FOR RESET */
  const prevPageRef = useRef(page);
  const { resetGameboard } = useScoreboard();

  useEffect(() => {
    if (page !== prevPageRef.current && pageKey === 'GAME_PLAY') {
      resetGameboard();
    }
    prevPageRef.current = page;
  }, [page, pageKey]);

  // in use for showing pause screen on interrupter unmount
  /* eslint-disable-next-line no-unused-vars */
  // const [, setIsInterrupterMounted] = useState(true);
  const gameCharacter = useStore((state) => state.gameCharacter);

  const tabBack = () => {
    setIsPauseMenuDisplayed(false);
    setIsPauseEnabled(false);
  };

  const alertMountedWarning = () => {
    // console.log(
    //   'alertMountedWarning :: pauseScreenDisabledPagesID',
    //   pauseScreenDisabledPagesID
    // );
    if (!pauseScreenDisabledPagesID.includes(page)) {
      // setIsInterrupterMounted(true);
    }
  };

  const orientationHandler = useCallback(
    () => {
      const portraitMode = isInPortrait();
      // update the store if the orientation has changed
      // if (inPortrait !== portraitMode) {
      setInPortrait(portraitMode);
      // }
      //Pause all video
      if (!portraitMode && play) {
        //debugger;
        setPlay(false);
      } else {
        if (!showSettingsPanel && !isPauseEnabled) {
          // when Settings Panel is not open & video is not previously paused
          setPlay(true);
        }
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [page, showSettingsPanel, isPauseEnabled]
  );

  const handleVisibilityChange = useCallback(
    () => {
      if (!audioCtxStarted) return;
      const audioCtxState = Tone.getContext().state;
      const audioCtxSuspended = audioCtxState === 'suspended';
      if (document.visibilityState === documentVisibilityState) return; // nothing to do
      setDocumentVisibilityState(document.visibilityState);
      if (document.visibilityState === 'hidden') {
        //debugger;
        setPlay(false);
        if (audioCtxSuspended) return;
        Tone.Transport.stop();
      } else {
        if (!showSettingsPanel && !isPauseEnabled && isInPortrait()) {
          // when Settings Panel is not open & video is not previously paused
          setPlay(true);
          if (audioCtxSuspended) return;
          Tone.Transport.start();
        }
      }
    },
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
    [
      page,
      audioCtxStarted,
      documentVisibilityState,
      showSettingsPanel,
      isPauseEnabled,
    ]
  );

  useEffect(() => {
    window.addEventListener('resize', orientationHandler);
    let hasChangeListener = false;
    if (window.screen.hasOwnProperty('orientation')) {
      hasChangeListener = true;
      window.screen.orientation.addEventListener('change', orientationHandler);
    }
    const portraitMode = isInPortrait();
    // update the store if the orientation has changed
    if (inPortrait !== portraitMode) {
      setInPortrait(portraitMode);
    }

    return () => {
      window.removeEventListener('resize', orientationHandler);
      if (hasChangeListener) {
        window.screen.orientation.removeEventListener(
          'change',
          orientationHandler
        );
      }
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [orientationHandler, handleVisibilityChange, inPortrait]);

  useEffect(() => {
    window.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [handleVisibilityChange]);

  const prevPagePauseScreenDisabledRef = useRef(
    pauseScreenDisabledPagesID.includes(page)
  );
  useEffect(() => {
    const handleVisChgPauseMenu = () => {
      const currPagePauseScreenDisabled =
        pauseScreenDisabledPagesID.includes(page);
      if (
        document.visibilityState === documentVisibilityState &&
        currPagePauseScreenDisabled !== prevPagePauseScreenDisabledRef.current
      )
        return; // nothing to do
      if (document.visibilityState === 'visible') {
        if (!pauseScreenDisabledPagesID.includes(page)) {
          setIsPauseMenuDisplayed(true);
          setIsPauseEnabled(true);
          setPlay(false);
        }
      } else {
        setIsPauseMenuDisplayed(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisChgPauseMenu);

    return () => {
      document.removeEventListener('visibilitychange', handleVisChgPauseMenu);
    };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [page]);

  useEffect(() => {
    orientationHandler();
    handleVisibilityChange();
  }, [orientationHandler, handleVisibilityChange]);

  useEffect(() => {
    setUserId(new URLSearchParams(window.location.search).get('userId'));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const settingsPanelPlayStateOnOpenRef = useRef(false);

  useEffect(() => {
    /**
     * @todo Restore the previous video player state instead of forcing play
     * @returns {void}
     */
    const setPlayability = () => {
      if (isPauseEnabled) return; // if video is already paused before settings panel opened, then avoid calling setPlay()
      if (showSettingsPanel && play === true) {
        settingsPanelPlayStateOnOpenRef.current = true;
        // when Settings Panel is open
        console.trace('setPlayability :: setPlay(false)');
        setPlay(false);
      } else if (
        !showSettingsPanel &&
        play === false &&
        settingsPanelPlayStateOnOpenRef.current
      ) {
        console.trace('setPlayability :: setPlay(true)');
        setPlay(true);
      }
    };
    setPlayability();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [showSettingsPanel, isPauseEnabled]);

  const mainContentHidden = !inPortrait || showSettingsPanel;
  useEffect(() => {
    document.body.classList.add(`market--${__t('market', 'GENERAL')}`);
  }, []);
  useEffect(() => {
    setTimeout(() => {
      focusSettingsToggler();
    }, 300);
  }, [page, isPauseEnabled, showSettingsPanel]);

  return (
    <>
      <main
        className={`game-wrapper ${pageKey} market--${__t(
          'market',
          'GENERAL'
        )} ${currentCueRef.current}`}
        key={'game-wrapper'}
        style={{
          height: `${viewport.height}px`,
        }}
      >
        <PageTitleAnnouncer />
        <SettingsButtonGradient />
        {inPortrait && (
          <NavBar
            hidden={mainContentHidden}
            slotConfig={{
              row: (
                <>
                  <SettingsDialog />
                  {!isPauseEnabled && (
                    <>
                      <HiddenH1 gameCharacter={gameCharacter} />
                      <PauseButton />
                      <SkipButton />
                    </>
                  )}
                  {/* <BackButton onClick={(evt) => goToPrevPage()} /> */}
                </>
              ),
            }}
          />
        )}
        <div
          hidden={mainContentHidden}
          aria-hidden={mainContentHidden}
          style={{
            height: '100%',
            width: '100%',
            display: mainContentHidden ? 'none' : 'block',
          }}
        >
          {/* {!mainContentHidden && !isPauseEnabled && props.children} */}
          <div
            id="Page-Wrapper"
            style={{
              height: '100%',
              width: '100%',
              display: isPauseEnabled ? 'none' : 'block',
              position: 'absolute',
            }}
          >
            {props.children}
          </div>
          <VIE
            videoRef={videoRef}
            InterfaceVideo={currentVIEConfig?.video}
            ToyVideo={currentVIEConfig?.toyVideo}
            Metadata={currentVIEConfig?.metadata}
            Captions={currentVIEConfig?.captions}
            Descriptions={currentVIEConfig?.descriptions}
            TextOverlays={currentVIEConfig?.legal}
            Templates={currentVIEConfig?.templates}
            PlayerConfig={__t('VIE')}
            Overlays={currentVIEConfig?.overlays}
            onEnded={currentVIEConfig?.onEnded || null}
            chapterOverride={currentVIEConfig?.skipChapter}
            setBeatsList={currentVIEConfig?.setBeatsList}
            Poster={currentVIEConfig?.Poster}
            hide={currentVIEConfig?.hide}
            onNextPage={() => goToNextPage()}
            onPrevPage={() => goToPrevPage()}
            onGoToPage={(v: string | number) => goToPage(v)}
            onCueChange={(v: any) => {
              console.log('VIE.onCueChange', v);
              currentCueRef.current = v[0].id;
            }}
          />
        </div>
        {/* <TestingNav
          pageIndex={page}
          pagesMap={pagesMap}
          goToPrevPage={goToPrevPage}
          goToNextPage={goToNextPage}
        /> */}
        {isPauseEnabled && !showSettingsPanel && (
          <PauseGeneral tabBack={tabBack} />
        )}
        {!inPortrait && (
          <OrientationChangeReminder
            alertMountedWarning={alertMountedWarning}
            alertUnMountedWarning={alertMountedWarning}
          />
        )}
        {/* <LanguageSwitcher hidden={mainContentHidden} aria-hidden={true} /> */}
      </main>
    </>
  );
};
